import type { HarnessId } from "../providerEvents/harnessEventTypes"
import { harnessIdForProvider } from "../modelRegistry"
import type { TaskRecord, TaskSessionRef } from "../persistence/records"
import type { IsolationStrategy, Task, TaskDeviceEnvironment, TaskProviderSessionReference, TaskWorkingDirectoryLocation, User } from "../types"

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

function optionalString(value: unknown): string | undefined {
    return typeof value === "string" ? value : undefined
}

function optionalBoolean(value: unknown): boolean | undefined {
    return typeof value === "boolean" ? value : undefined
}

function optionalStringArray(value: unknown): string[] | undefined {
    return Array.isArray(value) && value.every((item) => typeof item === "string") ? value : undefined
}

function optionalWorkingDirectoryLocation(value: unknown): TaskWorkingDirectoryLocation | undefined {
    return value === "primary" || value === "worktree" ? value : undefined
}

// Legacy read-only projection. New task records omit isolationStrategy.
function isolationStrategyFromRecord(value: unknown): IsolationStrategy {
    if (!isRecord(value)) return { type: "head" }
    if (value.type === "worktree" && typeof value.sourceBranch === "string") {
        return { type: "worktree", sourceBranch: value.sourceBranch }
    }
    return { type: "head" }
}

// Legacy read-only projection. New task records omit deviceEnvironments.
function taskDeviceEnvironmentFromRecord(value: unknown): TaskDeviceEnvironment | null {
    if (!isRecord(value)) return null
    const id = optionalString(value.id)
    const deviceId = optionalString(value.deviceId)
    const createdAt = optionalString(value.createdAt)
    const lastUsedAt = optionalString(value.lastUsedAt)
    const setupComplete = optionalBoolean(value.setupComplete)
    if (!id || !deviceId || !createdAt || !lastUsedAt || setupComplete === undefined) return null
    return {
        id,
        deviceId,
        setupComplete,
        createdAt,
        lastUsedAt,
        worktreeDir: optionalString(value.worktreeDir),
        mergeBaseCommit: optionalString(value.mergeBaseCommit),
        activeLocation: optionalWorkingDirectoryLocation(value.activeLocation),
    }
}

function taskDeviceEnvironmentsFromRecord(record: TaskRecord): TaskDeviceEnvironment[] {
    const value = record.deviceEnvironments
    if (!Array.isArray(value)) return []
    return value.map(taskDeviceEnvironmentFromRecord).filter((environment): environment is TaskDeviceEnvironment => environment !== null)
}

function pullRequestFromRecord(record: TaskRecord): Task["pullRequest"] | undefined {
    const linked = record.externalLinks?.find((candidate) => candidate.kind === "pull_request" || candidate.kind === "merge_request")
    if (linked) {
        return { id: linked.id, url: linked.url, provider: linked.provider, number: linked.number, watch: linked.watch }
    }
    const value = record.pullRequest
    if (!isRecord(value)) return undefined
    const url = optionalString(value.url)
    const provider = optionalString(value.provider)
    if (!url || (provider !== "github" && provider !== "gitlab" && provider !== "other")) return undefined
    const number = typeof value.number === "number" ? value.number : undefined
    return { url, provider, ...(number !== undefined ? { number } : {}) }
}

export function harnessIdFromSessionRef(session: TaskSessionRef): HarnessId {
    return harnessIdForProvider(session.providerId) ?? "claude-code"
}

export function providerSessionRefFromRecord(session: TaskSessionRef): TaskProviderSessionReference {
    const harnessId = harnessIdFromSessionRef(session)
    return {
        harnessId,
        providerId: session.providerId,
        providerSessionId: session.providerSessionId ?? session.nestSessionId ?? "",
        nestSessionId: session.nestSessionId,
        parentSessionId: session.parentSessionId,
        predecessorSessionId: typeof session.predecessorSessionId === "string" ? session.predecessorSessionId : undefined,
        label: session.label,
        cwd: session.cwd,
        mode: session.mode === "full" || session.mode === "read-only" ? session.mode : undefined,
        model: session.model,
        thinking: session.thinking,
        startTurnId: session.startTurnId,
        lastTurnId: session.lastTurnId,
        lastUsedAt: session.updatedAt,
        role: session.role,
    }
}

export function continuationProviderSession(
    providerSessions: readonly TaskProviderSessionReference[],
    activeSessionId?: string
): TaskProviderSessionReference | null {
    if (activeSessionId) return providerSessions.find((session) => session.nestSessionId === activeSessionId) ?? null
    const resumable = providerSessions.filter((session) => !!session.nestSessionId)
    const primary = resumable.filter((session) => session.role === "primary")
    const historicalRoots = resumable.filter((session) => !session.parentSessionId)
    const candidates = primary.length > 0 ? primary : historicalRoots.length > 0 ? historicalRoots : resumable
    const ranked = candidates
        .map((session, index) => ({ session, index }))
        .sort((a, b) => {
            const timeCompare = (a.session.lastUsedAt ?? "").localeCompare(b.session.lastUsedAt ?? "")
            return timeCompare === 0 ? a.index - b.index : timeCompare
        })
    return ranked.at(-1)?.session ?? null
}

export function taskFromProductRecord(record: TaskRecord, currentUser: User): Task {
    const sessionIds: Record<string, string> = {}
    const providerSessions = record.sessions.map((session) => {
        const harnessId = harnessIdFromSessionRef(session)
        if (session.nestSessionId) sessionIds[harnessId] = session.nestSessionId
        return providerSessionRefFromRecord(session)
    })

    const task: Task = {
        id: record.id,
        repoId: record.repoId,
        slug: optionalString(record.slug) ?? record.id,
        title: record.title,
        description: optionalString(record.description) ?? "",
        isolationStrategy: isolationStrategyFromRecord(record.isolationStrategy),
        deviceEnvironments: taskDeviceEnvironmentsFromRecord(record),
        createdBy: currentUser,
        events: [],
        comments: record.comments ?? [],
        sessionIds,
        providerSessions,
        activeSessionId: record.activeSessionId,
        createdAt: record.createdAt,
        updatedAt: record.updatedAt,
        closed: record.closed,
        queuePriority: record.queuePriority,
        attention: record.attention,
    }

    const enabledMcpServerIds = optionalStringArray(record.enabledMcpServerIds)
    if (enabledMcpServerIds) task.enabledMcpServerIds = enabledMcpServerIds
    const pullRequest = pullRequestFromRecord(record)
    if (pullRequest) task.pullRequest = pullRequest
    if (record.pullRequestAutomation) task.pullRequestAutomation = { ...record.pullRequestAutomation }
    if (record.agentGitContext) task.agentGitContext = { ...record.agentGitContext }
    if (record.directorySelection) task.directorySelection = { ...record.directorySelection }
    return task
}
